// Updated FeaturedItem.js with mobile-responsive styling

import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Users, Package, Navigation, MapPin, ChevronLeft, ChevronRight, Eye, Heart, ShoppingCart, Download, Check, Plus, Info, X } from 'lucide-react';
import OrderChat from '../../components/Chat/OrderChat';
import { getSubdomainUrl } from '../../utils/subdomainRouter';
import { needsOrderForm } from '../../utils/orderFormConfig';
import { formatShopPrice } from '../../utils/currency';
import { formatCityStateLabel } from '../../utils/locationUtils';
import { useAuth } from '../../contexts/AuthContext';
import { queueViewIncrement } from '../../firebase/firebaseService';

const GOOGLE_LOGO = 'https://cdn.cdnlogo.com/logos/g/35/google-icon.svg';

// Seller-chosen CTA button text for "sell service" (offer-tab) cards —
// set via item.ctaStyle in the shop editor. 'signup' pairs with
// item.externalLink to redirect off-site instead of opening the order flow.
const CTA_LABELS = { order: 'Order', book: 'Book', signup: 'Sign Up' };

// Welcoming ease-in for card grids — callers stagger it via an inline
// `animationDelay` (see ShopPublicView's featured/category grids).
const itemCardFadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;


const CategoryBadge = styled.div`
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  background: ${props => `${props.theme?.colors?.accent || '#800000'}90`};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 2;
  backdrop-filter: blur(4px);
  
  /* Mobile responsive */
  @media (max-width: 480px) {
    top: 0.5rem;
    left: 0.5rem;
    padding: 0.2rem 0.5rem;
    font-size: 0.65rem;
    border-radius: 8px;
  }
`;

const ViewCount = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.7rem;
  color: ${props => props.theme?.colors?.accent || props.theme?.colors?.text || '#FFFFFF'};
  opacity: 0.75;
  white-space: nowrap;
  flex-shrink: 0;
  
  svg {
    width: 12px;
    height: 12px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.65rem;
    gap: 0.25rem;

    svg {
      width: 11px;
      height: 11px;
    }
  }

  ${props => props.$big && `
    font-size: 0.95rem;
    gap: 0.4rem;

    svg {
      width: 16px;
      height: 16px;
    }
  `}

  ${props => props.$fontSize && `font-size: ${props.$fontSize};`}
  ${props => props.$iconSize && `svg { width: ${props.$iconSize}; height: ${props.$iconSize}; }`}
`;

const CompactOrderButton = styled.button`
  background: ${props => props.theme?.colors?.accent || '#800000'};
  color: white;
  border: none;
  padding: 0.35rem 0.6rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.7rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (hover: hover) {
    &:hover {
      background: ${props => props.theme?.colors?.primary || '#4A0404'};
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 20px;
    height: 12px;
    pointer-events: none;
  }
  
  @media (max-width: 480px) {
    padding: 0.3rem 0.5rem;
    font-size: 0.65rem;
    
    svg {
      width: 14px;
      height: 11px;
    }
  }
`;

const ItemInfo = styled.div`
  padding: ${p => p.$oneCol ? '1.1rem 0.5rem 1.1rem 0.35rem' : p.$compact ? '0.75rem 0.3rem 0.75rem 0.15rem' : '0.75rem'};
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: ${props => `${props.theme?.colors?.surface || 'rgba(255, 255, 255, 0.05)'}`};
  border-top: 1px solid ${props => `${props.theme?.colors?.accent}15` || 'rgba(255, 255, 255, 0.05)'};

  .top-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
  }

  .item-name {
    font-size: 0.95rem;
    color: ${props => props.theme?.colors?.text || '#FFFFFF'};
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    word-break: break-word;
    font-family: ${props => props.theme?.fonts?.heading || 'inherit'};
    font-weight: 600;
    flex: 1;
    line-height: 1.2;
  }

  .price {
    font-size: 0.95rem;
    color: ${props => props.theme?.colors?.accent || '#800000'};
    font-weight: bold;
    white-space: nowrap;
    flex-shrink: 0;
  }
  
  .engagement-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.5rem;
    padding-top: 0.25rem;
    position: relative;
    border-top: 1px solid ${props => `${props.theme?.colors?.accent}10` || 'rgba(255, 255, 255, 0.05)'};
  }
  
  /* NEW: Centered order button container */
  .centered-order-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
  }
  
  @media (max-width: 768px) {
    padding: ${p => p.$oneCol ? '0.8rem 0.4rem 0.8rem 0.28rem' : p.$compact ? '0.55rem 0.22rem 0.55rem 0.12rem' : '0.5rem'};
    gap: 0.35rem;

    .item-name {
      font-size: 0.68rem;
    }

    .price {
      font-size: 0.68rem;
    }
  }

  @media (max-width: 480px) {
    padding: ${p => p.$oneCol ? '0.75rem 0.38rem 0.75rem 0.25rem' : p.$compact ? '0.5rem 0.2rem 0.5rem 0.1rem' : '0.45rem'};
    gap: 0.35rem;

    .top-row {
      align-items: flex-start;
    }

    .item-name {
      font-size: 0.64rem;
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      word-break: break-word;
    }

    .price {
      font-size: 0.64rem;
      align-self: flex-start;
    }
  }
`;

// UPDATE OrderButton - make it compact and centered
const OrderButton = styled.button`
  background: ${props => props.theme?.colors?.accent || '#800000'};
  color: white;
  border: none;
  padding: 0.45rem 1rem;
  border-radius: 15px;
  font-weight: 600;
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  transition: all 0.3s ease;
  white-space: nowrap;
  position: relative;
  z-index: 10;
  width: 50%;
  min-width: 100px;
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (hover: hover) {
    &:hover {
      background: ${props => props.theme?.colors?.primary || '#4A0404'};
      box-shadow: 0 4px 8px ${props => `${props.theme?.colors?.accent}40` || 'rgba(128, 0, 0, 0.3)'};
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 14px;
    height: 14px;
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    padding: 0.32rem 0.7rem;
    font-size: 0.6rem;
    min-width: 72px;
  }

  @media (max-width: 480px) {
    padding: 0.28rem 0.55rem;
    font-size: 0.56rem;
    min-width: 65px;

    svg {
      width: 10px;
      height: 10px;
    }
  }
`;

const BottomRowContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
`;

const GoogleOrderSlideUp = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 0.5rem 0.6rem 0.6rem;
  background: ${props => `${props.theme?.colors?.background || '#000000'}F2`};
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-top: 1px solid ${props => `${props.theme?.colors?.accent}28` || 'rgba(255,255,255,0.1)'};
  z-index: 20;
  transform: translateY(${props => props.$show ? '0' : '100%'});
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: ${props => props.$show ? 'auto' : 'none'};
`;


const ExpandedActionButtons = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.5rem;
  z-index: 5;
  
  @media (max-width: 480px) {
    bottom: 0.75rem;
    left: 0.75rem;
    right: 0.75rem;
    gap: 0.4rem;
  }
`;

const ChatOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: ${props => props.isOpen ? 1 : 0};
  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
  transition: opacity 0.3s ease;
`;

const ItemCard = styled.div`
  background: ${props => props.theme?.colors?.surface || 'rgba(0, 0, 0, 0.4)'};
  border-radius: ${props => props.theme?.styles?.borderRadius || '12px'};
  overflow: hidden;
  border: 1px solid ${props => `${props.theme?.colors?.accent}30` || 'rgba(255, 255, 255, 0.1)'};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
  animation: ${itemCardFadeIn} 0.5s cubic-bezier(0.4, 0, 0.2, 1) both;
  
  /* ✅ FIXED: Let card height adapt to content naturally */
  height: 100%;
  
  &:hover {
    transform: translateY(-5px);
    border-color: ${props => props.theme?.colors?.accent || '#800000'};
    box-shadow: 0 5px 15px ${props => `${props.theme?.colors?.accent || 'rgba(128, 0, 0, 0.2)'}40`};
  }
  
  @media (max-width: 768px) {
    border-radius: 10px;
    
    &:hover {
      transform: translateY(-3px);
    }
  }
  
  @media (max-width: 480px) {
    border-radius: 8px;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 3px 10px ${props => `${props.theme?.colors?.accent || 'rgba(128, 0, 0, 0.2)'}30`};
    }
  }
  
  @media (max-width: 360px) {
    &:hover {
      transform: none;
      box-shadow: 0 2px 8px ${props => `${props.theme?.colors?.accent || 'rgba(128, 0, 0, 0.2)'}20`};
    }
  }
`;

// ADD this styled component in FeaturedItem.js
const FollowButton = styled.button`
  background: ${props => props.$isFollowing
    ? 'rgba(76,175,80,0.12)'
    : `${props.theme?.colors?.accent || '#800000'}18`};
  border: 1px solid ${props => props.$isFollowing
    ? 'rgba(76,175,80,0.5)'
    : `${props.theme?.colors?.accent || '#800000'}55`};
  border-radius: 20px;
  padding: 3px 10px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  white-space: nowrap;
  color: ${props => props.$isFollowing ? '#4CAF50' : props.theme?.colors?.accent || '#800000'};
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 5;
  opacity: ${props => props.$isLoading ? 0.5 : 1};

  &:hover {
    background: ${props => props.$isFollowing
      ? 'rgba(76,175,80,0.22)'
      : `${props.theme?.colors?.accent || '#800000'}30`};
  }

  &:active {
    transform: scale(0.96);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  svg {
    width: 13px;
    height: 13px;
    pointer-events: none;
  }
`;


const InfoButton = styled.button`
  position: absolute;
  bottom: 4.5rem;
  right: 1rem;
  background: ${props => `${props.theme?.colors?.background || 'rgba(0, 0, 0, 0.8)'}DD`};
  backdrop-filter: blur(10px);
  border: 1px solid ${props => `${props.theme?.colors?.accent}40` || 'rgba(255, 255, 255, 0.25)'};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme?.colors?.accent || '#800000'};
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 11;
  opacity: ${props => props.$isOpen ? 1 : 0.8};
  
  &:hover {
    opacity: 1;
    transform: scale(1.05);
    background: ${props => props.theme?.colors?.accent || '#800000'};
    color: white;
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    bottom: 4rem;
    right: 0.75rem;
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const InfoPanel = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  right: 5.5rem;
  background: ${props => `${props.theme?.colors?.background || 'rgba(0, 0, 0, 0.9)'}F5`};
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid ${props => `${props.theme?.colors?.accent}40` || 'rgba(255, 255, 255, 0.25)'};
  border-radius: 16px;
  padding: 1rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
  opacity: ${props => props.$isOpen ? 1 : 0};
  transform: translateY(${props => props.$isOpen ? '0' : '20px'});
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  transition: all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
  
  .info-content {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .description-section {
    font-size: 0.9rem;
    line-height: 1.5;
    color: ${props => props.theme?.colors?.text || 'white'};
    opacity: 0.95;
  }
  
  .engagement-section {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding-top: 0.75rem;
    border-top: 1px solid ${props => `${props.theme?.colors?.accent}20` || 'rgba(255, 255, 255, 0.1)'};
  }
  
  .like-button-info {
    background: transparent;
    border: none;
    color: ${props => props.$isLiked ? '#FF4444' : props.theme?.colors?.text || '#FFFFFF'};
    padding: 0.5rem 0.75rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.85rem;
    font-weight: 500;
    transition: all 0.2s ease;
    border-radius: 12px;
    
    &:hover {
      background: ${props => `${props.theme?.colors?.accent}10` || 'rgba(128, 0, 0, 0.1)'};
    }
    
    &:active {
      transform: scale(0.95);
    }
    
    svg {
      width: 16px;
      height: 16px;
      fill: ${props => props.$isLiked ? '#FF4444' : 'none'};
    }
  }
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => `${props.theme?.colors?.accent}60` || 'rgba(128, 0, 0, 0.6)'};
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    left: 0.75rem;
    right: 5rem;
    padding: 0.875rem;
    max-height: 180px;
    bottom: 0.75rem;
    
    .description-section {
      font-size: 0.85rem;
    }
    
    .like-button-info {
      font-size: 0.8rem;
      padding: 0.4rem 0.6rem;
      
      svg {
        width: 14px;
        height: 14px;
      }
    }
  }
`;

const ShopNameOverlay = styled.div`
  position: absolute;
  bottom: 0.3rem;
  left: 0.3rem;
  color: ${props => props.theme?.colors?.accent || '#800000'};
  font-size: 0.75rem;
  font-weight: 600;
  z-index: 2;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  max-width: 60%;
  
  &:hover {
    transform: scale(1.05);
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.9),
                 0 0 12px rgba(0, 0, 0, 0.7);
  }
  
  .shop-icon {
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.8));
  }
  
  .shop-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  @media (max-width: 480px) {
    top: 0.5rem;
    left: 0.5rem;
    font-size: 0.7rem;
    gap: 0.25rem;
    
    svg {
      width: 11px;
      height: 11px;
    }
  }
`;


const Distance = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: ${props => `${props.theme?.colors?.background || 'rgba(0, 0, 0, 0.8)'}CC`};
  padding: 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  color: ${props => props.theme?.colors?.text || 'white'};
  
  /* Mobile responsive */
  @media (max-width: 480px) {
    top: 0.75rem;
    right: 0.75rem;
    padding: 0.4rem;
    font-size: 0.75rem;
    border-radius: 3px;
  }
  
  @media (max-width: 360px) {
    top: 0.5rem;
    right: 0.5rem;
    padding: 0.3rem;
    font-size: 0.7rem;
  }
`;

const LocationText = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme?.colors?.text || 'rgba(255, 255, 255, 0.7)'};
  opacity: 0.7;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  /* Mobile responsive */
  @media (max-width: 480px) {
    font-size: 0.8rem;
    gap: 0.4rem;
  }
  
  @media (max-width: 360px) {
    font-size: 0.75rem;
    gap: 0.3rem;
  }
`;

const ExpandedContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  
  .expanded-header {
    position: relative;
    height: 300px;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    
    .carousel-controls {
      position: absolute;
      bottom: 1rem;
      left: 0;
      right: 0;
      display: flex;
      justify-content: center;
      gap: 0.5rem;
    }
  }
  
  .expanded-body {
    padding: 1.5rem;
  }
  
  .expanded-title {
    font-size: 1.8rem;
    font-family: ${props => props.theme?.fonts?.heading || 'inherit'};
    margin-bottom: 0.5rem;
    color: ${props => props.theme?.colors?.accent || '#800000'};
  }
  
  .expanded-price {
    font-size: 1.4rem;
    font-weight: bold;
    margin-bottom: 1rem;
  }
  
  .expanded-description {
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }
  
  .expanded-location {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
    opacity: 0.8;
  }
  
  /* Mobile responsive */
  @media (max-width: 480px) {
    .expanded-header {
      height: 250px;
    }
    
    .expanded-body {
      padding: 1rem;
    }
    
    .expanded-title {
      font-size: 1.5rem;
    }
    
    .expanded-price {
      font-size: 1.2rem;
    }
    
    .expanded-description {
      font-size: 0.9rem;
      line-height: 1.5;
    }
    
    .expanded-location {
      font-size: 0.8rem;
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  
  /* Mobile responsive */
  @media (max-width: 480px) {
    gap: 0.75rem;
    margin-top: 1rem;
  }
  
  @media (max-width: 360px) {
    gap: 0.5rem;
    margin-top: 0.75rem;
  }
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 0.75rem;
  border-radius: ${props => props.theme?.styles?.borderRadius || '8px'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &.primary {
    background: ${props => props.theme?.colors?.accent || '#800000'};
    color: white;
    border: none;
    
    &:hover {
      background: ${props => props.theme?.colors?.primary || '#4A0404'};
    }
  }
  
  &.secondary {
    background: transparent;
    border: 1px solid ${props => props.theme?.colors?.accent || '#800000'};
    color: ${props => props.theme?.colors?.accent || '#800000'};
    
    &:hover {
      background: ${props => `${props.theme?.colors?.accent}10` || 'rgba(128, 0, 0, 0.1)'};
    }
  }
  
  /* Mobile responsive */
  @media (max-width: 480px) {
    padding: 0.6rem;
    font-size: 0.8rem;
    gap: 0.4rem;
    border-radius: 6px;
    
    svg {
      width: 14px;
      height: 14px;
    }
  }
  
  @media (max-width: 360px) {
    padding: 0.5rem;
    font-size: 0.75rem;
    gap: 0.3rem;
    border-radius: 4px;
    
    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const ZoomContainer = styled.div`
  position: relative;
  z-index: 10001;
  border-radius: ${props => props.theme?.styles?.borderRadius || '12px'};
  overflow: hidden;
  border: 2px solid ${props => props.theme?.colors?.accent || '#800000'};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.6);
  background: ${props => props.theme?.colors?.background || '#000000'};
  max-height: 95vh;
  max-width: 95vw;
  width: 600px;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  
  /* Mobile responsive */
  @media (max-width: 768px) {
    width: 90vw;
    max-height: 90vh;
    border-radius: 8px;
  }
  
  @media (max-width: 480px) {
    width: 95vw;
    max-height: 95vh;
    border-radius: 6px;
    border-width: 1px;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => `${props.theme?.colors?.background || 'rgba(0, 0, 0, 0.5)'}80`};
  z-index: 9999;
  backdrop-filter: blur(2px);
`;

// UPDATE LikeButton - center it in grid
const LikeButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.liked ? '#FF4444' : props.theme?.colors?.text || '#FFFFFF'};
  padding: 0.4rem 0.6rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.75rem;
  font-weight: 500;
  transition: all 0.2s ease;
  opacity: ${props => props.liked ? 1 : 0.7};
  white-space: nowrap;
  flex-shrink: 0;
  position: relative;
  z-index: 10;
  justify-self: center; /* NEW: Center in grid */
  border-radius: 16px;
  
  &:hover {
    opacity: 1;
    transform: scale(1.05);
    background: ${props => `${props.theme?.colors?.accent}10` || 'rgba(128, 0, 0, 0.1)'};
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    width: 14px;
    height: 14px;
    fill: ${props => props.liked ? '#FF4444' : 'none'};
    transition: fill 0.2s ease;
    pointer-events: none;
  }
  
  span {
    pointer-events: none;
  }
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
    padding: 0.35rem 0.5rem;
    
    svg {
      width: 13px;
      height: 13px;
    }
  }
`;

// NEW: Location Container - holds nav button + distance
const LocationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  justify-self: end; /* NEW: Align to right in grid */
`;

// NEW: Navigation Button
const NavigationButton = styled.button`
  background: transparent;
  border: none;
  padding: 0.25rem;
  cursor: ${props => props.disabled ? 'default' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  position: relative;
  opacity: ${props => props.disabled ? 0.6 : 0.8};
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    opacity: 1;
    background: ${props => `${props.theme?.colors?.accent}15` || 'rgba(128, 0, 0, 0.1)'};
    transform: scale(1.1);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.9);
  }
  
  svg {
    width: 14px;
    height: 14px;
    color: ${props => 
      props.locationSource === 'gps' ? '#4CAF50' :
      props.locationSource === 'ip' ? props.theme?.colors?.accent :
      props.theme?.colors?.text
    };
    animation: ${props => props.locationSource === 'requesting' ? 'pulse 1.5s ease-in-out infinite' : 'none'};
    transition: color 0.3s ease;
  }
  
  /* Location quality indicator dot */
  &::after {
    content: '';
    position: absolute;
    top: 1px;
    right: 1px;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${props => 
      props.locationSource === 'gps' ? '#4CAF50' : 
      props.locationSource === 'ip' ? '#FFC107' : '#999'
    };
    border: 1px solid white;
    box-shadow: 0 0 3px rgba(0, 0, 0, 0.3);
    opacity: ${props => props.locationSource ? 1 : 0};
    transition: all 0.3s ease;
  }
  
  @media (max-width: 480px) {
    padding: 0.2rem;
    
    svg {
      width: 13px;
      height: 13px;
    }
    
    &::after {
      width: 4px;
      height: 4px;
      top: 0px;
      right: 0px;
    }
  }
`;

const TopBar = styled.div`
  padding: 0.45rem 0.8rem;
  background: transparent;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.45rem;
  min-height: 34px;
  flex-shrink: 0;

  .shop-pfp {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
    border: 1px solid ${props => `${props.theme?.colors?.accent}40` || 'rgba(255, 255, 255, 0.25)'};
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      border-radius: 50%;
      display: block;
    }
  }
  
  .shop-name {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    white-space: normal;
    word-break: break-word;
    font-size: clamp(0.55rem, 1.8vw, 0.72rem);
    font-weight: 600;
    color: ${props => props.theme?.colors?.accent || '#800000'};
    cursor: pointer;
    transition: all 0.2s ease;
    flex: 1;
    min-width: 0;
    line-height: 1.2;
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover {
      opacity: 0.8;
    }
  }

  /* ✅ NEW: Container for shop info on the left */
  .shop-info-left {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    flex: 1;
    overflow: hidden;
    min-width: 0;
  }

  @media (max-width: 768px) {
    padding: 0.35rem 0.65rem;
    min-height: 28px;
    height: auto;

    .shop-name {
      font-size: clamp(0.5rem, 2vw, 0.68rem);
    }
  }

  @media (max-width: 480px) {
    padding: 0.3rem 0.6rem;

    .shop-name {
      font-size: clamp(0.48rem, 2.5vw, 0.65rem);
    }
  }

  /* Desktop 1x gets a bigger top bar than desktop 2x/3x (which use the base size
     above). Static attribute selector (rather than a props => css\`\` branch) so
     styled-components only computes this rule once instead of re-evaluating it
     on every render of every card — this component renders on every welcome-page
     card (showTopBar defaults true there), so that cost adds up fast. */
  @media (min-width: 769px) {
    &[data-big="true"] {
      padding: 0.55rem 0.9rem;
      gap: 0.5rem;
      min-height: 40px;

      .shop-pfp {
        width: 32px;
        height: 32px;
      }

      .shop-name {
        font-size: clamp(0.55rem, 1.7vw, 0.70rem);
      }
    }
  }
`;

const ImageSection = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 5 / 6;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => `${props.theme?.colors?.background || '#000000'}50`};
  flex-shrink: 0;
  flex-grow: 1;

  @media (min-width: 769px) {
    aspect-ratio: 5 / 4.373;
  }

  /* ADD: Blurred background layer */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: ${props => (props.$blurDataURL || props.$bgImage) ? `url(${props.$blurDataURL || props.$bgImage})` : 'none'};
    background-size: cover;
    background-position: center;
    filter: blur(20px);
    transform: scale(1.1);
    z-index: 0;
    opacity: 0.6;
  }

  @keyframes imageZoomPulse {
    0%   { transform: scale(1); }
    50%  { transform: scale(1.27); }
    100% { transform: scale(1); }
  }

  img, video {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    position: relative;
    z-index: 1;
    animation: ${props => props.$isVisible && !props.$isService ? 'imageZoomPulse 14s ease-in-out infinite' : 'none'};
    transform-origin: center center;
  }
  
  video {
    background: transparent; /* CHANGED from #000 */
  }

  .carousel-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: ${props => `${props.theme?.colors?.background || 'rgba(0, 0, 0, 0.5)'}90`};
    border: 1px solid ${props => `${props.theme?.colors?.accent || 'rgba(255, 255, 255, 0.2)'}40`};
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.theme?.colors?.text || 'white'};
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 2;

    &:hover {
      opacity: 1;
      background: ${props => `${props.theme?.colors?.accent || 'rgba(0, 0, 0, 0.8)'}40`};
    }

    &.left {
      left: 1rem;
    }

    &.right {
      right: 1rem;
    }
  }
  
  &:hover .carousel-arrow {
    opacity: 0.7;
  }
  
  @media (max-width: 768px) {
    aspect-ratio: 4 / 3;
    
    .carousel-arrow {
      width: 28px;
      height: 28px;
      
      &.left {
        left: 0.75rem;
      }
      
      &.right {
        right: 0.75rem;
      }
      
      svg {
        width: 14px;
        height: 14px;
      }
    }
  }
  
  @media (max-width: 480px) {
    aspect-ratio: 1 / 1;
    
    .carousel-arrow {
      width: 24px;
      height: 24px;
      
      &.left {
        left: 0.5rem;
      }
      
      &.right {
        right: 0.5rem;
      }
      
      svg {
        width: 12px;
        height: 12px;
      }
    }
    
    &:not(:hover) .carousel-arrow {
      opacity: 0.5;
    }
  }
`;

const TopBarDistance = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  background: ${props => `${props.theme?.colors?.background || 'rgba(0, 0, 0, 0.6)'}CC`};
  backdrop-filter: blur(8px);
  padding: 0.25rem 0.5rem;
  border-radius: 10px;
  border: 1px solid ${props => `${props.theme?.colors?.accent}30` || 'rgba(255, 255, 255, 0.2)'};
  flex-shrink: 0;
  
  .distance-icon {
    color: ${props => 
      props.locationSource === 'gps' ? '#4CAF50' :
      props.locationSource === 'ip' ? '#FFC107' :
      props.theme?.colors?.accent || '#800000'
    };
    display: flex;
    align-items: center;
  }
  
  .distance-text {
    font-size: 0.7rem;
    font-weight: 600;
    color: ${props => props.theme?.colors?.text || 'white'};
    white-space: nowrap;
  }
  
  @media (max-width: 480px) {
    padding: 0.2rem 0.4rem;
    
    .distance-text {
      font-size: 0.65rem;
    }
    
    svg {
      width: 10px;
      height: 10px;
    }
  }
`;


// UPDATE DistanceTag - just the text now
const DistanceTag = styled.span`
  font-size: 0.75rem;
  color: ${props => props.theme?.colors?.text || '#FFFFFF'};
  opacity: 0.9;
  white-space: nowrap;
  font-weight: 500;
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
  }
`;

const shouldDisplayItem = (item) => {
  // ✅ NEW: Filter by store status
  if (item.storeOpen === false) {
    console.log('🚫 Store closed:', item.shopId);
    return false;
  }
  
  // ✅ NEW: Filter by item visibility (eye icon)
  if (item.visible === false) {
    console.log('🚫 Item hidden (eye closed):', item.id);
    return false;
  }
  
  // ... rest of existing filters (hidden, deleted, paused, etc.)
  if (item.hidden) {
    console.log('🚫 Item hidden (paused):', item.id);
    return false;
  }
  
  // ✅ Filter out deleted items
  if (item.deleted) {
    console.log('🚫 Item deleted:', item.id);
    return false;
  }
  
  // ✅ Filter out items from paused shops
  if (item.shopStatus === 'paused') {
    console.log('🚫 Shop paused:', item.shopId);
    return false;
  }
  
  // ✅ Filter out items from deleted shops
  if (item.shopStatus === 'deleted') {
    console.log('🚫 Shop deleted:', item.shopId);
    return false;
  }
  
  // ✅ Filter out if account status is not active
  if (item.accountStatus && item.accountStatus !== 'active') {
    console.log('🚫 Account not active:', item.accountStatus);
    return false;
  }
  
  return true;
};



// UPDATE the FeaturedItem component with new props
const FeaturedItem = ({
  item,
  showDistance,
  theme,
  onItemClick,
  onOrderClick,
  onLikeClick,
  onAddToCart,
  onFollowToggle,
  showTopBar = true,
  showShopName = false,
  compactBottom = false,
  isAuthenticated = false,
  locationSource,
  onRequestLocation,
  columns = 2,
  disableZoom = false,
  style,
}) => {
  const is3Col = columns >= 3;
  // Single-column ("1x") cards on mobile get a taller bottom bar with bigger
  // text, since there's plenty of width for it — only applies to the compact
  // welcome/public-page grid card, and only on mobile where 1x is actually used.
  const oneCol = columns === 1;
  const isMobileViewport = typeof window !== 'undefined' && window.innerWidth <= 768;
  const bigCompact = oneCol && isMobileViewport;
  // Desktop 1x gets the biggest bottom bar; desktop 2x (or 3x, which callers cap
  // to 2x on desktop) gets a smaller-but-still-bumped tier. Mobile keeps its own
  // separate sizing (bigCompact above, or the small default further down).
  const desktopBig = oneCol && !isMobileViewport;
  const desktop2x = !oneCol && !isMobileViewport;
  // Mobile 2-column grid card: no like button (view count takes its spot in
  // row 1), and the order button sits at the right of row 2 instead of centered.
  const mobile2x = columns === 2 && isMobileViewport;

    const { signInWithGoogle } = useAuth();

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFullyVisible, setIsFullyVisible] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [localViewCount, setLocalViewCount] = useState(item.viewCount || 0);
  const [showOrderPrompt, setShowOrderPrompt] = useState(false);
  const cardRef = useRef(null);
  const wasVisibleRef = useRef(false);
  const navigate = useNavigate();
  const itemTheme = theme || item.shopTheme || {};

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting && entry.intersectionRatio >= 0.95;
        setIsFullyVisible(visible);
        if (visible) {
          wasVisibleRef.current = true;
        } else if (wasVisibleRef.current && item.shopId && item.id) {
          wasVisibleRef.current = false;
          setLocalViewCount(c => c + 1);
          queueViewIncrement(item.shopId, item.id, !!(item.isService || item.jobType));
        }
      },
      { threshold: 0.95 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [item.shopId, item.id, item.isService, item.jobType]);

  const isVideoMedia = (media) => {
  if (!media) return false;
  // Check if it's an object with isVideo flag
  if (typeof media === 'object' && media.isVideo) return true;
  // Check if it's a string URL containing video indicators
  if (typeof media === 'string') {
    return media.includes('video-') || media.includes('%2Fvideo-') || 
           media.includes('.mp4') || media.includes('.webm') || media.includes('.mov');
  }
  return false;
};




  useEffect(() => {
    if (!showTopBar) {
      console.log(`📏 FeaturedItem ${item.id}:`, {
        showDistance,
        formattedDistance: item.formattedDistance,
        hasCoordinates: !!(item.coordinates?.lat && item.coordinates?.lng),
        locationSource
      });
    }
  }, [item.id, item.formattedDistance, showDistance, showTopBar, locationSource]);

  if (!shouldDisplayItem(item)) {
    return null;
  }

  const handleNextImage = (e) => {
    e.stopPropagation();
    const validImages = item.images?.filter(Boolean) || [];
    if (validImages.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % validImages.length);
    }
  };
  
  const handlePrevImage = (e) => {
    e.stopPropagation();
    const validImages = item.images?.filter(Boolean) || [];
    if (validImages.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + validImages.length) % validImages.length);
    }
  };
  
  const getDisplayImage = () => {
  const validImages = item.images?.filter(Boolean) || [];
  if (validImages.length > 0) {
    const media = validImages[currentImageIndex % validImages.length];
    // Return the media object/string as-is (we'll handle type detection in render)
    return media;
  }
  return null;
};

  // Rate suffix for service cards — hourly/daily bookings show their unit, flat-rate & gig-claim jobs show none.
  const getPriceSuffix = () => {
    if (!item.isService || (item.serviceTab || 'offer') === 'need') return '';
    if (item.pricingType === 'daily') return '/day';
    if (item.pricingType === 'total') return '';
    return '/hr';
  };

  // Service with no price → show "Get a Quote" instead of a price and a "Book" button.
  const isQuote = item.isService && (item.serviceTab || 'offer') !== 'need' && (!item.price || parseFloat(item.price) === 0);

  const handleClick = (e) => {
    e.stopPropagation();
    if (showOrderPrompt) { setShowOrderPrompt(false); return; }
    if (onItemClick) onItemClick(item);
  };

  const handleOrderClick = (e) => {
    e.stopPropagation();
    if (item.ctaStyle === 'signup' && item.externalLink) {
      window.open(item.externalLink, '_blank', 'noopener,noreferrer');
      return;
    }
    if (!isAuthenticated && !needsOrderForm(item)) { setShowOrderPrompt(true); return; }
    if (onOrderClick) onOrderClick(item, null);
    else if (onAddToCart) onAddToCart(item, null);
    else if (onItemClick) onItemClick(item);
  };

  const handleLikeClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onLikeClick) {
      onLikeClick(item, isAuthenticated);
    }
  };

  // NEW: Handle navigation button click
  const handleNavigationClick = (e) => {
    e.stopPropagation();
    if (onRequestLocation && locationSource !== 'gps' && locationSource !== 'requesting') {
      onRequestLocation();
    }
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (item.ctaStyle === 'signup' && item.externalLink) {
      window.open(item.externalLink, '_blank', 'noopener,noreferrer');
      return;
    }
    if (!isAuthenticated && !needsOrderForm(item)) { setShowOrderPrompt(true); return; }
    if (onAddToCart) onAddToCart(item, null);
    else if (onOrderClick) onOrderClick(item, null);
  };

  const handleConfirmGoogleOrder = (e) => {
    e.stopPropagation();
    setShowOrderPrompt(false);
    if (onAddToCart) onAddToCart(item, null);
    else if (onOrderClick) onOrderClick(item, null);
    else if (onItemClick) onItemClick(item);
  };

  const getShopLogo = () => {
  // If shop has profile picture, use it
  if (item.shopProfilePicture) {
    return item.shopProfilePicture;
  }
  // Otherwise use the default logo from src/pages/shop/
  return '/logo512.png';
};

  const isOutOfStock = item.isService ? 
    parseInt(item.slots || 0) < 1 : 
    parseInt(item.quantity || 0) < 1;

  return (
    <ItemCard
      ref={cardRef}
      onClick={handleClick}
      theme={itemTheme}
      className={`item-card-${item.id}`}
      style={style}
    >

{showTopBar && (
  <TopBar theme={itemTheme} data-big={desktopBig ? 'true' : undefined}>
    {/* LEFT SIDE: pfp (+ shop name when showShopName) */}
    <div className="shop-info-left">
      <div
        className="shop-pfp"
        onClick={(e) => { e.stopPropagation(); if (item.shopId) navigate(`/shop/${item.shopId}/view`); }}
        title={item.shopName || 'Shop'}
      >
        <img
          src={getShopLogo()}
          alt={item.shopName || 'Shop Logo'}
          onError={(e) => {
            e.target.style.display = 'none';
            const parent = e.target.parentElement;
            parent.textContent = item.shopName ? item.shopName.charAt(0).toUpperCase() : 'S';
            parent.style.display = 'flex';
            parent.style.background = itemTheme?.colors?.accent || '#800000';
            parent.style.color = 'white';
            parent.style.fontSize = '10px';
            parent.style.fontWeight = 'bold';
          }}
          style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%' }}
        />
      </div>
      {showShopName && (
        <div
          className="shop-name"
          onClick={(e) => { e.stopPropagation(); if (item.shopId) navigate(`/shop/${item.shopId}/view`); }}
        >
          <span>{item.shopName || 'Shop'}</span>
        </div>
      )}
    </div>

    {/* RIGHT SIDE: Follow button */}
    {!is3Col && onFollowToggle && (
      <button
        onClick={(e) => { e.stopPropagation(); onFollowToggle(item.shopId, e); }}
        disabled={!!item.isFollowLoading}
        title={item.isFollowing ? 'Following' : 'Follow'}
        style={{
          background: item.isFollowing ? '#4CAF50' : itemTheme?.colors?.accent || '#800000',
          border: `1px solid ${item.isFollowing ? '#4CAF50' : itemTheme?.colors?.accent || '#800000'}`,
          borderRadius: '50%',
          width: '22px',
          height: '22px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: item.isFollowLoading ? 'not-allowed' : 'pointer',
          color: 'white',
          opacity: item.isFollowLoading ? 0.6 : 1,
          flexShrink: 0,
          transition: 'all 0.2s ease',
          marginLeft: 'auto',
        }}
      >
        {item.isFollowing ? <Check size={12} /> : <Plus size={12} />}
      </button>
    )}
  </TopBar>
)}

      <ImageSection
        theme={itemTheme}
        $isVisible={isFullyVisible && !disableZoom}
        $isService={!!(item.isService || item.jobType)}
        $wide={['rides','moving','delivery'].includes(item.jobType)}
        $bgImage={(() => {
          const currentMedia = getDisplayImage();
          if (!currentMedia) return null;

          const isVideo = isVideoMedia(currentMedia);
          if (isVideo) return null;

          return typeof currentMedia === 'object' ? currentMedia.url : currentMedia;
        })()}
        $blurDataURL={item.imageBlur?.[currentImageIndex] || null}
      >
        {(() => {
          const currentMedia = getDisplayImage();
          
          if (!currentMedia) {
            return (
              <div style={{ width:'100%', height:'100%', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color: itemTheme?.colors?.text || '#FFFFFF', opacity: 0.3, position:'relative', zIndex:1 }}>
                <Package size={40} />
                <span style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>No Media</span>
              </div>
            );
          }
          
          const isVideo = isVideoMedia(currentMedia);
          
          if (isVideo) {
            const videoSrc = typeof currentMedia === 'object' ? currentMedia.url : currentMedia;
            
            return (
              <video
                src={videoSrc}
                loop
                muted
                playsInline
                autoPlay
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block',
                  position: 'relative',
                  zIndex: 1
                }}
                onError={(e) => {
                  console.error('Video failed to load:', videoSrc);
                  e.target.style.display = 'none';
                  const parent = e.target.parentElement;
                  if (parent) {
                    parent.innerHTML = `
                      <div style="width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; color: ${itemTheme?.colors?.text || '#FFF'}; opacity: 0.3; position: relative; z-index: 1;">
                        <span>Video unavailable</span>
                      </div>
                    `;
                  }
                }}
              />
            );
          }
          
          const fullSrc = typeof currentMedia === 'object' ? currentMedia.url : currentMedia;
          // Grid cards download the small thumbnail instead of the full-size
          // image when one exists; legacy items with no thumbnail fall back
          // to the full image exactly as before.
          const imageSrc = item.thumbnails?.[currentImageIndex] || fullSrc;

          return (
            <img
              src={imageSrc}
              alt={item.name}
              loading="lazy"
              decoding="async"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                objectPosition: 'center',
                display: 'block',
                position: 'relative',
                zIndex: 1,
                opacity: 0,
                transition: 'opacity 0.2s ease',
              }}
              onLoad={(e) => { e.target.style.opacity = '1'; }}
              onError={(e) => {
                console.error('Image failed to load:', imageSrc);
                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23333" width="200" height="200"/%3E%3Ctext fill="%23666" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                e.target.style.opacity = '1';
              }}
            />
          );
        })()}

        {/* Ride route — navigate button (bottom-left) + distance/duration pills */}
        {item.jobType === 'rides' && (item.pickupAddress || item.destinationAddress) && (() => {
          const enc = encodeURIComponent;
          const from = item.pickupCoordinates?.lat
            ? `${item.pickupCoordinates.lat},${item.pickupCoordinates.lng}`
            : enc(item.pickupAddress || '');
          const to = item.destinationCoordinates?.lat
            ? `${item.destinationCoordinates.lat},${item.destinationCoordinates.lng}`
            : enc(item.destinationAddress || '');
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
          const mapsUrl = isIOS
            ? `maps://maps.apple.com/?saddr=${from}&daddr=${to}&dirflg=d`
            : `https://www.google.com/maps/dir/?api=1&origin=${from}&destination=${to}&travelmode=driving`;
          return (
            <>
              {/* Bottom-left: navigate button */}
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                style={{
                  position: 'absolute', bottom: 6, left: 6, zIndex: 3,
                  width: 28, height: 28, borderRadius: '50%',
                  background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(6px)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', textDecoration: 'none',
                }}
              >
                <Navigation size={13} />
              </a>

            </>
          );
        })()}

        {/* Moving: navigate button (bottom-left) + distance pill */}
        {item.jobType === 'moving' && (item.pickupAddress || item.destinationAddress) && (() => {
          const enc = encodeURIComponent;
          const from = item.pickupCoordinates?.lat ? `${item.pickupCoordinates.lat},${item.pickupCoordinates.lng}` : enc(item.pickupAddress || '');
          const to = item.destinationCoordinates?.lat ? `${item.destinationCoordinates.lat},${item.destinationCoordinates.lng}` : enc(item.destinationAddress || '');
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
          const mapsUrl = isIOS ? `maps://maps.apple.com/?saddr=${from}&daddr=${to}&dirflg=d` : `https://www.google.com/maps/dir/?api=1&origin=${from}&destination=${to}&travelmode=driving`;
          return (
            <>
              <a href={mapsUrl} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()} style={{ position:'absolute', bottom:6, left:6, zIndex:3, width:28, height:28, borderRadius:'50%', background:'rgba(0,0,0,0.72)', backdropFilter:'blur(6px)', border:'1px solid rgba(255,255,255,0.18)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', textDecoration:'none' }}><Navigation size={13}/></a>
            </>
          );
        })()}

        {/* Delivery: navigate button (bottom-left) */}
        {item.jobType === 'delivery' && (item.pickupAddress || item.deliveryAddress) && (() => {
          const enc = encodeURIComponent;
          const from = item.pickupCoordinates?.lat ? `${item.pickupCoordinates.lat},${item.pickupCoordinates.lng}` : enc(item.pickupAddress || '');
          const to = item.deliveryCoordinates?.lat ? `${item.deliveryCoordinates.lat},${item.deliveryCoordinates.lng}` : enc(item.deliveryAddress || '');
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
          const mapsUrl = isIOS ? `maps://maps.apple.com/?saddr=${from}&daddr=${to}&dirflg=d` : `https://www.google.com/maps/dir/?api=1&origin=${from}&destination=${to}&travelmode=driving`;
          return (
            <>
              <a href={mapsUrl} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()} style={{ position:'absolute', bottom:6, left:6, zIndex:3, width:28, height:28, borderRadius:'50%', background:'rgba(0,0,0,0.72)', backdropFilter:'blur(6px)', border:'1px solid rgba(255,255,255,0.18)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', textDecoration:'none' }}><Navigation size={13}/></a>
              {item.fragile && <div style={{ position:'absolute', bottom:6, left:'50%', transform:'translateX(-50%)', zIndex:3, background:'rgba(220,50,50,0.8)', backdropFilter:'blur(6px)', borderRadius:20, padding:'2px 8px', fontSize:'0.58rem', fontWeight:700, color:'#fff', pointerEvents:'none', whiteSpace:'nowrap', width:'max-content' }}>⚠️ Fragile</div>}
            </>
          );
        })()}

        {/* Events: nav button only — details shown in zoomed overlay */}
        {item.jobType === 'events' && item.eventAddress && (() => {
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
          const mapsUrl = isIOS ? `maps://maps.apple.com/?q=${encodeURIComponent(item.eventAddress)}` : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(item.eventAddress)}`;
          return <a href={mapsUrl} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()} style={{ position:'absolute', top:6, right:6, zIndex:3, width:28, height:28, borderRadius:'50%', background:'rgba(0,0,0,0.72)', backdropFilter:'blur(6px)', border:'1px solid rgba(255,255,255,0.18)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', textDecoration:'none' }}><Navigation size={13}/></a>;
        })()}

        {/* Digital product badge */}
        {item.isDigital && (
          <div style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', background: 'rgba(0,180,100,0.85)', color: '#fff', padding: '0.2rem 0.5rem', borderRadius: '8px', fontSize: '0.65rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.3rem', zIndex: 2, backdropFilter: 'blur(4px)', letterSpacing: '0.5px' }}>
            <Download size={10} />
            DIGITAL
          </div>
        )}

        {/* City badge — bottom-left. Shows the item's city instead of its
            distance (distance still drives "nearby" sorting/categorization
            upstream, just isn't shown here). Skipped for ride/moving/delivery/
            events job types, which already have their own nav button there. */}
        {!['rides', 'moving', 'delivery', 'events'].includes(item.jobType) && formatCityStateLabel(item) && (
          <div style={{
            position: 'absolute', bottom: 6, left: 6, zIndex: 3,
            display: 'flex', alignItems: 'center', gap: '0.25rem',
            background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(6px)',
            border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: 20, padding: '0.2rem 0.55rem',
            color: '#fff', fontSize: '0.62rem', fontWeight: 600,
            whiteSpace: 'nowrap', pointerEvents: 'none',
          }}>
            <MapPin size={10} />
            {formatCityStateLabel(item)}
          </div>
        )}

        {/* Carousel arrows */}
        {(item.images?.filter(Boolean).length > 1) && (
          <>
            <button className="carousel-arrow left" onClick={handlePrevImage}>
              <ChevronLeft size={16} />
            </button>
            <button className="carousel-arrow right" onClick={handleNextImage}>
              <ChevronRight size={16} />
            </button>
          </>
        )}

      </ImageSection>

      {/* Bottom Info */}
      <ItemInfo theme={itemTheme} $compact={showTopBar || compactBottom} $oneCol={bigCompact}>
        {(!showTopBar && !compactBottom) ? (
          /* Shop page full layout: name + price row + centered order button */
          <>
            <div className="top-row">
              <h3 className="item-name">{item.name}</h3>
              <div className="price">
                {isQuote ? null : `${formatShopPrice(item.price, item.currency)}${getPriceSuffix()}`}
              </div>
            </div>
            <div className="centered-order-container">
              <OrderButton theme={itemTheme} onClick={handleOrderClick} disabled={isOutOfStock}>
                <ShoppingCart size={14} />
                {isOutOfStock ? 'Unavailable' : item.isDigital ? 'Buy & Download' : item.jobType === 'rides' ? 'Claim' : item.serviceTab === 'need' ? 'Claim' : item.isService && CTA_LABELS[item.ctaStyle] ? CTA_LABELS[item.ctaStyle] : item.isService ? 'Book' : 'Order'}
              </OrderButton>
            </div>
          </>
        ) : (
          /* Welcome page: name row + compact engagement row */
          <>
          {/* Row 1: name | like (+ view stacked only in 3col) */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: desktopBig ? '6.84px' : bigCompact ? '8px' : desktop2x ? '3.29px' : '4px' }}>
            <div style={{
              fontSize: desktopBig ? '0.836rem' : bigCompact ? '1.05rem' : desktop2x ? '0.62rem' : '0.75rem',
              fontWeight: '600',
              color: itemTheme?.colors?.text || '#FFFFFF',
              lineHeight: 1.3,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              wordBreak: 'break-word',
              flex: 1,
            }}>
              {item.jobType && (
                <span style={{ marginRight: '3px' }}>
                  {({rides:'🚗',home_repair:'🔧',cleaning:'🧹',moving:'📦',delivery:'🚚',tutoring:'📚',pet_care:'🐾',handyman:'🛠️',beauty:'💇',tech:'💻',events:'🎉',other:'💼'})[item.jobType] || '💼'}
                </span>
              )}
              {item.name}
            </div>
            {/* Like (+ view counter only in 3col; in non-3col view count moves to bottom bar).
                Mobile 2x drops the like button entirely — view count takes its spot instead. */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: desktopBig ? '3.42px' : bigCompact ? '4px' : desktop2x ? '1.68px' : '2px', flexShrink: 0 }}>
              {!mobile2x && (
                <button
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: item.isLiked ? itemTheme?.colors?.accent : `${itemTheme?.colors?.accent || itemTheme?.colors?.text}90`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: desktopBig ? '3.42px' : bigCompact ? '4px' : desktop2x ? '1.68px' : '2px',
                    cursor: 'pointer',
                    fontSize: desktopBig ? '0.76rem' : bigCompact ? '0.95rem' : desktop2x ? '0.56rem' : '0.68rem',
                    fontWeight: '500',
                    padding: '0',
                    transition: 'all 0.2s ease',
                  }}
                  onClick={handleLikeClick}
                  title={isAuthenticated ? (item.isLiked ? 'Unlike' : 'Like') : 'Login to like'}
                >
                  <Heart size={desktopBig ? 13 : bigCompact ? 16 : desktop2x ? 9 : 11} fill={item.isLiked ? 'currentColor' : 'none'} />
                  <span>{item.likeCount || item.likesCount || 0}</span>
                </button>
              )}
              {(is3Col || mobile2x) && (
                <ViewCount theme={itemTheme}>
                  <Eye size={11} />
                  <span>{localViewCount}</span>
                </ViewCount>
              )}
            </div>
          </div>


          {/* Row 2: price left | order center | view count right (non-3col); price left | order right (3col) */}
          {(() => {
            const accentHex = (itemTheme?.colors?.accent || '#800000').replace('#', '');
            const r = parseInt(accentHex.substr(0,2), 16) || 0;
            const g = parseInt(accentHex.substr(2,2), 16) || 0;
            const b = parseInt(accentHex.substr(4,2), 16) || 0;
            const btnTextColor = (0.299*r + 0.587*g + 0.114*b) / 255 > 0.5 ? '#000' : '#fff';
            const JOB_CLAIM_LABELS = { rides:'Request', moving:'Request', delivery:'Request', events:'Book', beauty:'Request', tech:'Request', pet_care:'Request', tutoring:'Book', home_repair:'Request', cleaning:'Request', handyman:'Request', other:'Request' };
            const label = isOutOfStock ? 'Unavailable' : item.serviceTab === 'need' ? (JOB_CLAIM_LABELS[item.jobType] || 'Claim') : item.isService && CTA_LABELS[item.ctaStyle] ? CTA_LABELS[item.ctaStyle] : item.isService ? 'Book' : 'Order';
            const btnStyle = {
              background: isOutOfStock ? 'rgba(255,255,255,0.1)' : (itemTheme?.colors?.accent || '#800000'),
              border: 'none',
              color: isOutOfStock ? 'rgba(255,255,255,0.6)' : btnTextColor,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '0.3em',
              cursor: isOutOfStock ? 'not-allowed' : 'pointer',
              opacity: isOutOfStock ? 0.5 : 1,
              padding: desktopBig ? '0.418em 0.836em' : bigCompact ? '0.5em 1em' : desktop2x ? '0.35em 0.7em' : '0.3em 0.7em',
              borderRadius: '999px',
              fontSize: desktopBig ? 'clamp(0.608rem, 3.192vw, 0.836rem)' : desktop2x ? 'clamp(0.455rem, 2.45vw, 0.658rem)' : is3Col ? 'clamp(0.44rem, 2.2vw, 0.62rem)' : bigCompact ? 'clamp(0.66rem, 3.28vw, 0.86rem)' : 'clamp(0.55rem, 3vw, 0.8rem)',
              fontWeight: '700', letterSpacing: '0.02em',
              whiteSpace: 'nowrap', overflow: 'hidden', maxWidth: '100%', minWidth: 0,
            };
            return (
              <div className="engagement-row" style={{
                display: 'grid',
                // 1x (mobile or desktop) and desktop 2x: equal 1fr side columns so the
                // button sits at the true center of the row regardless of price/view-count
                // text width. Mobile 3x keeps the original auto-middling layout. Mobile 2x:
                // price left, order button right (view count already moved up to row 1).
                gridTemplateColumns: mobile2x ? 'auto 1fr' : is3Col ? 'auto auto' : (bigCompact || desktopBig || desktop2x) ? '1fr auto 1fr' : 'auto minmax(0, 1fr) auto',
                justifyContent: is3Col ? 'space-between' : 'stretch',
                alignItems: 'center',
                gap: desktopBig ? '6.84px' : bigCompact ? '8px' : desktop2x ? '3.29px' : '4px',
                padding: '0',
                overflow: 'hidden',
              }}>
                {/* Left: price — natural width only */}
                <span style={{ justifySelf: 'start', fontSize: desktopBig ? '0.836rem' : desktop2x ? '0.66rem' : is3Col ? '0.72rem' : bigCompact ? '0.86rem' : '0.8rem', fontWeight: '700', color: itemTheme?.colors?.accent || '#800000', whiteSpace: 'nowrap' }}>
                  {isQuote ? null : item.price ? `${formatShopPrice(item.price, item.currency)}${getPriceSuffix()}` : 'Make offer'}
                </span>

                {/* Middle (non-3col): button centered in the gap between price and view count.
                    Mobile 2x has no third column, so it's pinned right instead. */}
                <button
                  style={{ ...btnStyle, justifySelf: mobile2x ? 'end' : 'center' }}
                  onClick={(e) => { e.stopPropagation(); handleAddToCart(e); }}
                  disabled={isOutOfStock}
                  title={label}
                >
                  <ShoppingCart size='1em' style={{ flexShrink: 0, width: '1em', height: '1em' }} />
                  {label}
                </button>

                {/* Right: view count — natural width, right-aligned. Skipped on mobile 2x,
                    where the view count already lives up in row 1. */}
                {!is3Col && !mobile2x && (
                  <ViewCount
                    theme={itemTheme}
                    $big={bigCompact || desktopBig || desktop2x}
                    $fontSize={desktopBig ? '0.836rem' : desktop2x ? '0.66rem' : null}
                    $iconSize={desktopBig ? '14px' : desktop2x ? '11px' : null}
                    style={{ justifySelf: 'end' }}
                  >
                    <Eye size={10} />
                    <span>{localViewCount}</span>
                  </ViewCount>
                )}
              </div>
            );
          })()}
          </>
        )}
      </ItemInfo>

      {!needsOrderForm(item) && (
        <GoogleOrderSlideUp theme={itemTheme} $show={showOrderPrompt} onClick={e => e.stopPropagation()}>
          <button
            onClick={handleConfirmGoogleOrder}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.45rem',
              width: '100%', padding: '0.55rem',
              background: `${itemTheme?.colors?.accent || '#800000'}22`,
              border: `1.5px solid ${itemTheme?.colors?.accent || '#800000'}`,
              borderRadius: 10,
              color: itemTheme?.colors?.text || '#ffffff',
              fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer',
              letterSpacing: '0.02em',
            }}
          >
            <img src={GOOGLE_LOGO} alt="" style={{ width: 16, height: 16, flexShrink: 0 }} />
            Order with Google
          </button>
        </GoogleOrderSlideUp>
      )}
    </ItemCard>
  );
};

export default React.memo(FeaturedItem);